'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import { Markdown } from 'src/components/markdown';

import { BlogCTA } from './components/blog-cta';
import { PostItem } from './components/post-item';
import { PostTags } from './components/post-tags';
import { BLOG_DETAILS } from './constants/blog-copy';
import { pickRecentPosts } from './utils/blog-recent-posts';
import { PostDetailsHero } from './components/post-details-hero';
import { CM, SECTION_PX, PUBLIC_TEXT, BLOG_PAGE_SX, BLOG_READ_WIDTH, PUBLIC_MAX_WIDTH, BLOG_RECENT_GRID_SX } from './constants/blog-layout';

import type { BlogPost } from './scope/blog-post-scope';

// ----------------------------------------------------------------------

type PostDetailsHomeViewProps = {
  /** 라우트가 서버에서 `getPost(id)` 로 받아 넘긴다. 없으면 라우트가 Error 를 그린다. */
  post: BlogPost;
  latestPosts: BlogPost[];
};

/**
 * **`/post/[id]`** — 블로그 글 하나. 머리 · 본문 · 주제 · 「최근 글」 · 맨 아래 띠.
 * 글과 최근 글은 라우트가 서버에서 받아 넘기고, 이 뷰는 조회하지 않는다.
 */
export function PostDetailsHomeView({ post, latestPosts }: PostDetailsHomeViewProps) {
  return (
    <Box sx={BLOG_PAGE_SX}>
      <Box component="article" sx={{ px: SECTION_PX, pt: { xs: 3, md: 4 } }}>
        <PostDetailsHero
          title={post?.title ?? ''}
          author={post?.author}
          coverUrl={post?.coverUrl ?? ''}
          createdAt={post?.createdAt}
          tags={post?.tags ?? []}
        />

        <Box sx={{ maxWidth: BLOG_READ_WIDTH, mx: 'auto', mt: { xs: 3, md: 4 } }}>
          <Markdown children={post?.content} />

          <PostTags tags={post?.tags ?? []} />
        </Box>
      </Box>

      {!!latestPosts?.length && (
        <Box component="section" sx={{ px: SECTION_PX, pt: { xs: 6, md: 8 } }}>
          <Box sx={{ maxWidth: PUBLIC_MAX_WIDTH, mx: 'auto' }}>
            <Typography component="h2" sx={{ ...PUBLIC_TEXT.subtitle, color: CM.textDark, mb: { xs: 2, md: 3 } }}>
              {BLOG_DETAILS.recentTitle}
            </Typography>
            <Box sx={BLOG_RECENT_GRID_SX}>
              {pickRecentPosts(latestPosts).map((latestPost: BlogPost) => (
                <PostItem key={latestPost.id} post={latestPost} detailsHref={paths.post.details(latestPost.id)} />
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <BlogCTA />
    </Box>
  );
}
